import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getHibernateStats, type HibernateStatsResponse } from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { FleetHibernateChart } from "@admin/components/charts/HibernateCharts";
import { isoRangeForPreset, RangePicker, type RangePreset } from "@admin/components/RangePicker";

function defaultCustomDates(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60_000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function HibernationPage() {
  const [preset, setPreset] = useState<RangePreset>("7d");
  const customDefaults = useMemo(() => defaultCustomDates(), []);
  const [customFrom, setCustomFrom] = useState(customDefaults.from);
  const [customTo, setCustomTo] = useState(customDefaults.to);
  const [data, setData] = useState<HibernateStatsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const query = useMemo(() => {
    if (preset === "custom") {
      if (!customFrom || !customTo) return null;
      return { from: customFrom, to: customTo };
    }
    return isoRangeForPreset(preset);
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    if (!query) return;
    setErr(null);
    getHibernateStats(query.from, query.to)
      .then(setData)
      .catch(() => setErr("Could not load hibernation history"));
  }, [query]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Hibernation</h1>
        <RangePicker
          preset={preset}
          customFrom={customFrom}
          customTo={customTo}
          onPreset={setPreset}
          onCustomFrom={setCustomFrom}
          onCustomTo={setCustomTo}
        />
      </div>

      {err ? <p className="text-danger">{err}</p> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-fg">Running</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{data?.current.runningCount ?? "-"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-fg">Hibernated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{data?.current.hibernatedCount ?? "-"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-fg">Waking</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{data?.current.wakingCount ?? "-"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-fg">Paused</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">{data?.current.pausedCount ?? "-"}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Fleet up / down</CardTitle>
        </CardHeader>
        <CardContent>
          <FleetHibernateChart data={data?.series ?? []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent events</CardTitle>
        </CardHeader>
        <CardContent>
          {!data || data.recentEvents.length === 0 ? (
            <p className="text-sm text-muted-fg">No hibernate or wake events in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border text-muted-fg">
                  <tr>
                    <th className="py-2 pr-3 font-medium">When</th>
                    <th className="py-2 pr-3 font-medium">User</th>
                    <th className="py-2 pr-3 font-medium">Event</th>
                    <th className="py-2 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentEvents.map((row) => (
                    <tr key={row.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 text-muted-fg">{formatDate(row.occurredAt)}</td>
                      <td className="py-2 pr-3">
                        <Link to={`/users/${row.userId}`} className="text-accent hover:underline">
                          {row.email}
                        </Link>
                      </td>
                      <td className="py-2 pr-3">{row.kind}</td>
                      <td className="py-2">{row.reason ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
